// src/components/SessionMonitor.jsx
import React, { useEffect, useState, useRef } from "react";
import SessionWarningDialog from "./SessionWarningDialog";
import { axiosRequest } from "../axiosConfig";
import {
  clearAllCookies,
  getCookie,
  redirectToLogin,
  setSessionDialogActive,
} from "../Utils";

const SessionMonitor = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const warningDisplayed = useRef(false);
  const logoutTimeout = useRef(null);
  const refreshAttempts = useRef(0);
  const MAX_REFRESH_ATTEMPTS = 3;
  const REFRESH_RETRY_DELAY = 1000; // 1 second

  const WARNING_THRESHOLD = 5 * 60 * 1000; // 5 minutes

  const refreshSession = async (retries = MAX_REFRESH_ATTEMPTS) => {
    try {
      const response = await axiosRequest.post("/session/refresh");
      setShowWarning(false);
      setSessionDialogActive(false);
      warningDisplayed.current = false;
      refreshAttempts.current = 0;

      if (logoutTimeout.current) {
        clearTimeout(logoutTimeout.current);
        logoutTimeout.current = null;
      }
      return true;
    } catch (error) {
      console.error("Failed to refresh session:", error);
      if (retries > 0) {
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, REFRESH_RETRY_DELAY));
        return refreshSession(retries - 1);
      }
      return false;
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    setSessionDialogActive(false);
    clearAllCookies();
    redirectToLogin();
  };

  const checkSession = async () => {
    if (getCookie("firstName")) {
      try {
        const response = await axiosRequest.get("/session/check");
        const timeRemaining = response.data.timeRemaining;

        if (timeRemaining < WARNING_THRESHOLD && !warningDisplayed.current) {
          warningDisplayed.current = true;
          setRemainingTime(timeRemaining);
          setShowWarning(true);
          setSessionDialogActive(true);

          // Set a timeout to logout when session expires
          if (logoutTimeout.current) {
            clearTimeout(logoutTimeout.current);
          }
          logoutTimeout.current = setTimeout(handleLogout, timeRemaining);
        }
      } catch (error) {
        // Only redirect if it's a 401 error and we've exceeded refresh attempts
        if (error.response?.status === 401 && refreshAttempts.current >= MAX_REFRESH_ATTEMPTS) {
          handleLogout();
        } else if (error.response?.status === 401) {
          refreshAttempts.current++;
        }
      }
    }
  };

  useEffect(() => {
    const intervalId = setInterval(checkSession, WARNING_THRESHOLD);

    return () => {
      clearInterval(intervalId);
      if (logoutTimeout.current) {
        clearTimeout(logoutTimeout.current);
      }
    };
  }, []);

  return (
    <SessionWarningDialog
      isVisible={showWarning}
      remainingTime={remainingTime}
      onExtendSession={refreshSession}
      onLogout={handleLogout}
    />
  );
};

export default SessionMonitor;
